import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Custom hook for WebSocket connection with:
 * - Automatic reconnection
 * - Heartbeat/ping mechanism
 * - Error recovery
 * - Proper cleanup
 * - Connection state management
 */
export const useWebSocket = (url, options = {}) => {
  const {
    onMessage,
    onOpen,
    onClose,
    onError,
    reconnectInterval = 3000,
    maxReconnectAttempts = 10,
    heartbeatInterval = 30000,
    connectionTimeout = 10000, // 10 seconds timeout for connection
  } = options;

  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isManualCloseRef = useRef(false);

  const [status, setStatus] = useState('connecting');
  const [lastError, setLastError] = useState(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  }, []);

  // Setup heartbeat
  const setupHeartbeat = useCallback(() => {
    cleanup();
    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        try {
          socketRef.current.send(JSON.stringify({ type: 'ping' }));
        } catch (error) {
          console.error('Error sending heartbeat:', error);
        }
      }
    }, heartbeatInterval);
  }, [heartbeatInterval, cleanup]);

  // Connect function
  const connect = useCallback(() => {
    if (!url) {
      setStatus('error');
      setLastError(new Error('WebSocket URL is required'));
      return;
    }

    // Close existing connection if any
    if (socketRef.current) {
      try {
        socketRef.current.close();
      } catch (e) {
        // Ignore errors when closing
      }
      socketRef.current = null;
    }

    try {
      isManualCloseRef.current = false;
      setStatus('connecting');
      
      const ws = new WebSocket(url);
      socketRef.current = ws;

      // Add a flag to track if connection was successful
      let connectionEstablished = false;

      // Set connection timeout - if not connected within timeout, close and retry
      connectionTimeoutRef.current = setTimeout(() => {
        if (!connectionEstablished && ws.readyState !== WebSocket.OPEN) {
          console.warn(`⏱️ Connection timeout after ${connectionTimeout}ms for ${url}`);
          ws.close();
          
          // Trigger reconnection
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            console.log(`🔄 Retrying connection ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${reconnectInterval}ms`);
            setStatus('reconnecting');
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, reconnectInterval);
          } else {
            setStatus('error');
            setLastError(new Error('Connection timeout - unable to reach server'));
          }
        }
      }, connectionTimeout);

      ws.onopen = (event) => {
        connectionEstablished = true;
        
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        setStatus('connected');
        setLastError(null);
        reconnectAttemptsRef.current = 0;
        console.log('🔌 WebSocket opened successfully:', url);
        setupHeartbeat();
        onOpen?.(event);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          // Ignore pong messages
          if (message.type !== 'pong') {
            onMessage?.(message, event);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          onMessage?.(null, event);
        }
      };

      ws.onclose = (event) => {
        // Only process if the socket is the current one
        if (socketRef.current !== ws) {
          console.log('⚠️ Ignoring close event from old socket');
          return;
        }

        console.log('🔌 WebSocket closed:', {
          url,
          code: event.code,
          reason: event.reason || 'No reason provided',
          wasClean: event.wasClean,
          connectionEstablished,
          isManualClose: isManualCloseRef.current,
          reconnectAttempts: reconnectAttemptsRef.current
        });

        setStatus('disconnected');
        cleanup();
        onClose?.(event);

        // Only attempt reconnection if connection was established and not manually closed
        if (
          connectionEstablished &&
          !isManualCloseRef.current &&
          reconnectAttemptsRef.current < maxReconnectAttempts
        ) {
          reconnectAttemptsRef.current += 1;
          console.log(`🔄 Attempting reconnection ${reconnectAttemptsRef.current}/${maxReconnectAttempts} in ${reconnectInterval}ms`);
          setStatus('reconnecting');
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectInterval);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error('❌ Max reconnection attempts reached');
          setStatus('error');
          setLastError(new Error('Max reconnection attempts reached'));
        } else if (!connectionEstablished) {
          console.log('⚠️ Connection was never established, not reconnecting');
        }
      };

      ws.onerror = (error) => {
        // Only process if the socket is the current one
        if (socketRef.current !== ws) {
          return;
        }
        
        setStatus('error');
        setLastError(error);
        console.error('WebSocket error:', error);
        onError?.(error);
      };
    } catch (error) {
      setStatus('error');
      setLastError(error);
      console.error('Error creating WebSocket:', error);
    }
  }, [url, onMessage, onOpen, onClose, onError, reconnectInterval, maxReconnectAttempts, connectionTimeout, setupHeartbeat, cleanup]);

  // Disconnect function
  const disconnect = useCallback(() => {
    isManualCloseRef.current = true;
    cleanup();
    if (socketRef.current) {
      try {
        // Only close if the connection is open or connecting
        if (socketRef.current.readyState === WebSocket.OPEN || 
            socketRef.current.readyState === WebSocket.CONNECTING) {
          socketRef.current.close();
        }
      } catch (e) {
        // Ignore errors when closing
      }
      socketRef.current = null;
    }
    setStatus('disconnected');
  }, [cleanup]);

  // Send message function
  const sendMessage = useCallback((message) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      try {
        const data = typeof message === 'string' ? message : JSON.stringify(message);
        socketRef.current.send(data);
        return true;
      } catch (error) {
        console.error('Error sending message:', error);
        return false;
      }
    }
    console.warn('WebSocket is not connected. Message not sent.');
    return false;
  }, []);

  // Connect on mount
  useEffect(() => {
    // Don't connect if URL is not provided
    if (!url) {
      setStatus('disconnected');
      return;
    }

    // Small delay to ensure component is fully mounted
    const mountTimer = setTimeout(() => {
      connect();
    }, 100);
    
    return () => {
      clearTimeout(mountTimer);
      isManualCloseRef.current = true;
      cleanup();
      
      // Close socket with a small delay to avoid race conditions
      const currentSocket = socketRef.current;
      if (currentSocket) {
        setTimeout(() => {
          try {
            // Only close if the connection is still in a closeable state
            if (currentSocket.readyState === WebSocket.OPEN || 
                currentSocket.readyState === WebSocket.CONNECTING) {
              currentSocket.close(1000, 'Component unmounting');
            }
          } catch (e) {
            // Ignore errors when closing
            console.log('Error closing socket on unmount:', e);
          }
        }, 50);
        socketRef.current = null;
      }
    };
    // Deliberately re-run only when `url` changes. `connect`/`cleanup` are
    // excluded because their identity churns whenever the caller passes new
    // inline onOpen/onClose/onError handlers, which would otherwise force a
    // reconnect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return {
    status,
    lastError,
    sendMessage,
    disconnect,
    reconnect: connect,
    isConnected: status === 'connected',
  };
};

